import {
  Button as MUIButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
} from "@mui/material";
import * as React from "react";

export interface ButtonProps extends MuiButtonProps {
  /**
   * If true, displays a loading spinner at the end of the button and disables
   * it, so an in-flight action cannot be fired twice.
   * The loading spinner replaces the endIcon when active.
   * @default false
   */
  loading?: boolean;
}

/**
 * A customized button component built on top of Material-UI Button.
 *
 * Features:
 * - Supports all Material-UI Button variants (text, contained, outlined)
 * - Supports all Material-UI Button sizes (small, medium, large)
 * - Includes a loading state with a circular progress indicator
 * - Removes elevation and text transformation by default for a cleaner look
 * - Forwards its ref to the underlying DOM button element
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Button variant="contained">Click me</Button>
 *
 * // With loading state
 * <Button loading={true} variant="outlined">Loading...</Button>
 *
 * // With custom size
 * <Button size="large" variant="text">Large Button</Button>
 * ```
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ loading, disabled, endIcon, sx, children, ...props }, ref) {
    return (
      <MUIButton
        ref={ref}
        disableElevation
        disabled={disabled || loading}
        endIcon={loading ? <CircularProgress size={"1rem"} /> : endIcon}
        {...props}
        sx={{ ...sx, textTransform: "none" }}
      >
        {children}
      </MUIButton>
    );
  },
);
